import { useEffect, useRef, useState } from 'react';

type ContactFormData = {
  name: string;
  email: string;
  company: string;
  subject: string;
  message: string;
};

type ContactModalProps = {
  open: boolean;
  onClose: () => void;
};

const initialFormData: ContactFormData = {
  name: '',
  email: '',
  company: '',
  subject: '',
  message: '',
};

export default function ContactModal({ open, onClose }: ContactModalProps) {
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setFormData(initialFormData);
      setIsSubmitting(false);
      setSubmitStatus('idle');
      setSubmitMessage('');
      setValidationError(null);
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstFieldRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidationError(null);
    setSubmitMessage('');

    const form = event.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        formName: 'tagIt Contact',
        page: typeof window !== 'undefined' ? window.location.href : 'homepage',
        ts: new Date().toISOString(),
        ...formData,
      };

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Contact endpoint responded ${response.status}`);
      }

      setSubmitStatus('success');
      setSubmitMessage('Message sent. We’ll get back to you soon.');
      setFormData(initialFormData);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error(error);
      }
      setSubmitStatus('error');
      const fallback = 'Something went wrong submitting your message. Please try again.';
      setSubmitMessage(fallback);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="contact-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Contact tagIt"
      onClick={onClose}
    >
      <div className="contact-modal-frame" ref={dialogRef} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="contact-modal-close" onClick={onClose} aria-label="Close contact form">
          ×
        </button>

        <div className="contact-modal-inner">
          <div className="contact-modal-copy">
            <h2>Start your AI journey today</h2>
            <p>
              Share the context, the team, and the outcome you’re aiming for. We’ll follow up with a thoughtful response
              and the next step that fits.
            </p>
            <div className="cta-fallback">
              Prefer email? Write us at{' '}
              <a className="link" href="mailto:get@tagit.live">
                get@tagit.live
              </a>
              .
            </div>
          </div>

          <form className="form contact-modal-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <label htmlFor="contact-name">Name *</label>
              <input
                ref={firstFieldRef}
                type="text"
                id="contact-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                autoComplete="name"
              />
            </div>

            <div className="form-row">
              <label htmlFor="contact-email">Email *</label>
              <input
                type="email"
                id="contact-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>

            <div className="form-row">
              <label htmlFor="contact-company">Company</label>
              <input
                type="text"
                id="contact-company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                disabled={isSubmitting}
                autoComplete="organization"
              />
            </div>

            <div className="form-row">
              <label htmlFor="contact-subject">Subject *</label>
              <input
                type="text"
                id="contact-subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-row">
              <label htmlFor="contact-message">Message *</label>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                placeholder="Tell us a little about the use case, timeline, or what you need help with."
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Send message'}
            </button>

            <div aria-live="polite" aria-atomic="true">
              {submitStatus === 'success' && <div className="form-message ok">{submitMessage}</div>}
              {submitStatus === 'error' && <div className="form-message err">{submitMessage}</div>}
              {validationError && <div className="form-message err">{validationError}</div>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
